import { useCallback, useMemo, useEffect } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { useFormik } from 'formik';
import * as yup from 'yup';
import { useTranslation } from 'react-i18next';

import { selectPayoutState } from '../selectors/payout.selector';
import { updatePayout, fetchPayout } from '../actions/payout.actions';
import { PayoutFormValues, GATEWAY_TYPE, PAYOUT_ACCOUNT_STATUS } from '../types/payout.types';
import {
    isvalidEnglishOnlyField,
    isValidBankAccountNo,
    isValidRoutingNumber,
    isValidForeignRoutingNumber,
    isValidSwiftCode
} from '../utils/validation';
import { useAlert } from './useAlert';

export const usePayoutForm = () => {
    const { t } = useTranslation();
    const dispatch = useDispatch();
    const { loading, updating, form } = useSelector(selectPayoutState);
    const alert = useAlert();

    useEffect(() => {
        // @ts-ignore
        dispatch(fetchPayout());
    }, [dispatch]);

    const validationSchema = useMemo(() => {
        const baseSchema = {
            gatewayType: yup.string().required(t('errors.epic.validation.required'))
        };

        const payPalSchema = {
            accountEmail: yup
                .string()
                .email(t('errors.epic.validation.email'))
                .when('gatewayType', {
                    is: GATEWAY_TYPE.PAYPAL,
                    then: (schema) => schema.required(t('errors.epic.validation.required')),
                    otherwise: (schema) => schema
                })
        };

        const bankSchema = {
            checkName: yup.string().when('gatewayType', {
                is: GATEWAY_TYPE.PAYPAL,
                then: (schema) => schema.nullable().notRequired(),
                otherwise: (schema) =>
                    schema
                        .required(t('errors.epic.validation.required'))
                        .max(
                            64,
                            t('errors.epic.validation.maxLength.v2', {
                                field: t('epic.esport.label.payoutInfo.checkName'),
                                maxLength: 64
                            })
                        )
                        .test(
                            'invalid',
                            t('errors.epic.validation.valied.v2', {
                                field: t('epic.esport.label.payoutInfo.checkName')
                            }),
                            (value) => (value ? isvalidEnglishOnlyField(value) : false)
                        )
            }),
            bankName: yup.string().when('gatewayType', {
                is: GATEWAY_TYPE.PAYPAL,
                then: (schema) => schema.nullable().notRequired(),
                otherwise: (schema) =>
                    schema
                        .required(t('errors.epic.validation.required'))
                        .max(
                            128,
                            t('errors.epic.validation.maxLength.v2', {
                                field: t('epic.esport.label.payoutInfo.bankName'),
                                maxLength: 128
                            })
                        )
                        .test(
                            'invalid',
                            t('errors.epic.validation.valied.v2', {
                                field: t('epic.esport.label.payoutInfo.bankName')
                            }),
                            (value) => (value ? isvalidEnglishOnlyField(value) : false)
                        )
            }),
            routingNo: yup.string().when(['gatewayType', 'foreignBank'], {
                is: (gatewayType: string, foreignBank: boolean) =>
                    (gatewayType === GATEWAY_TYPE.WIRE_TRANSFER && foreignBank) ||
                    gatewayType === GATEWAY_TYPE.PAYPAL,
                then: (schema) =>
                    schema
                        .nullable()
                        .notRequired()
                        .max(
                            32,
                            t('errors.epic.validation.maxLength.v2', {
                                field: t('epic.esport.label.payoutInfo.routingNo'),
                                maxLength: 32
                            })
                        )
                        .test(
                            'invalid',
                            t('errors.epic.validation.valied.v2', {
                                field: t('epic.esport.label.payoutInfo.routingNo')
                            }),
                            (value) => (value ? isValidForeignRoutingNumber(value) : true)
                        ),
                otherwise: (schema) =>
                    schema
                        .required(t('errors.epic.validation.required'))
                        .max(
                            32,
                            t('errors.epic.validation.maxLength.v2', {
                                field: t('epic.esport.label.payoutInfo.routingNo'),
                                maxLength: 32
                            })
                        )
                        .test(
                            'invalid',
                            t('errors.epic.validation.valied.v2', {
                                field: t('epic.esport.label.payoutInfo.routingNo')
                            }),
                            (value) => (value ? isValidRoutingNumber(value) : false)
                        )
            }),
            bankAccountNo: yup.string().when('gatewayType', {
                is: GATEWAY_TYPE.PAYPAL,
                then: (schema) => schema.nullable().notRequired(),
                otherwise: (schema) =>
                    schema
                        .required(t('errors.epic.validation.required'))
                        .max(
                            42,
                            t('errors.epic.validation.maxLength.v2', {
                                field: t('epic.esport.label.payoutInfo.bankAccountNo'),
                                maxLength: 42
                            })
                        )
                        .test(
                            'invalid',
                            t('errors.epic.validation.valied.v2', {
                                field: t('epic.esport.label.payoutInfo.bankAccountNo')
                            }),
                            (value) => {
                                if (value && isValidBankAccountNo(value)) {
                                    return true;
                                }
                                return false;
                            }
                        )
            }),
            swiftCode: yup
                .string()
                .max(
                    32,
                    t('errors.epic.validation.maxLength.v2', {
                        field: t('epic.esport.label.payoutInfo.swiftCode'),
                        maxLength: 32
                    })
                )
                .when(['gatewayType', 'foreignBank'], {
                    is: (gatewayType: string, foreignBank: boolean) =>
                        gatewayType === GATEWAY_TYPE.WIRE_TRANSFER && foreignBank,
                    then: (schema) => schema.required(t('errors.epic.validation.required')),
                    otherwise: (schema) => schema.nullable()
                })
                .test(
                    'invalid',
                    t('errors.epic.validation.valied.v2', {
                        field: t('epic.esport.label.payoutInfo.swiftCode')
                    }),
                    (value) => {
                        if (!value) return true;
                        return isValidSwiftCode(value);
                    }
                ),
            additionalAccountDetail: yup.string().when('gatewayType', {
                is: GATEWAY_TYPE.PAYPAL,
                then: (schema) => schema.nullable().notRequired(),
                otherwise: (schema) =>
                    schema.max(
                        1024,
                        t('errors.epic.validation.maxLength.v2', {
                            field: t('epic.esport.label.payoutInfo.additionalAccountDetail'),
                            maxLength: 1024
                        })
                    )
            })
        };

        return yup.object({
            ...baseSchema,
            ...payPalSchema,
            ...bankSchema
        });
    }, [t, form.bankAccountNo]);

    const initialValues: PayoutFormValues = {
        gatewayType: form.gatewayType || GATEWAY_TYPE.PAYPAL,
        accountEmail: form.accountEmail || '',
        foreignBank: form.foreignBank || false,
        checkName: form.checkName || '',
        bankName: form.bankName || '',
        routingNo: form.routingNo || '',
        bankAccountNo: form.bankAccountNo || '',
        swiftCode: form.swiftCode || '',
        additionalAccountDetail: form.additionalAccountDetail || ''
    };

    const formik = useFormik({
        initialValues,
        validationSchema,
        enableReinitialize: true,
        onSubmit: async (values: PayoutFormValues) => {
            const profile = {
                ...values,
                foreignTax: false,
                hideTax: true
            };
            // @ts-ignore
            const resp = await dispatch(updatePayout(profile));
            if (updatePayout.fulfilled.match(resp)) {
                alert.success(t('epic.esport.label.payoutInfo.offline.update.success'));
                return;
            }
            if (updatePayout.rejected.match(resp)) {
                alert.error(t('epic.common.network.error'));
            }
        }
    });

    const gatewayTypeOptions = useMemo(
        () => [
            {
                value: GATEWAY_TYPE.PAYPAL,
                label: t('epic.esport.label.payoutInfo.gatewayType.Paypal')
            },
            {
                value: GATEWAY_TYPE.WIRE_TRANSFER,
                label: t('epic.esport.label.payoutInfo.gatewayType.bank')
            }
        ],
        [t]
    );

    const selectedGatewayType = gatewayTypeOptions.find(
        (option) => option?.value === formik.values.gatewayType
    );

    const handleGatewayTypeChange = useCallback(
        (selected: any) => {
            formik.setFieldValue('gatewayType', selected?.value);
            formik.setFieldTouched('gatewayType', true, false);
            setTimeout(() => {
                formik.setTouched({}, false);
            }, 0);
        },
        [formik]
    );

    const handleChangeFormValues = useCallback(
        (_: string, evt: any) => {
            formik.handleChange(evt);
            formik.setFieldTouched(evt.target.name, true, false);
        },
        [formik]
    );

    const isSubmitDisable = useMemo(() => {
        return updating || form?.accountStatus === PAYOUT_ACCOUNT_STATUS.ACTIVE;
    }, [updating, formik]);

    const isEditModeDisable = useMemo(() => {
        return updating || form?.accountStatus === PAYOUT_ACCOUNT_STATUS.ACTIVE;
    }, [updating, formik]);

    return {
        formik,
        gatewayTypeOptions,
        loading,
        updating,
        handleGatewayTypeChange,
        handleChangeFormValues,
        selectedGatewayType,
        isSubmitDisable,
        isEditModeDisable
    };
};
